import { AnimatePresence } from 'framer-motion';
import { atom, useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import * as _ from 'lodash-es';
import type { Delta, Op } from 'quill/core';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as styles from './AiCopilotChatPanel.module.scss';
import Messages, {
  getMessageElementById,
  MESSAGES_LIST_PADDING_BOTTOM,
} from './Messages';
import type { Source, Tool } from './useAiCopilot';
import useAiCopilot from './useAiCopilot';
import RightPanel from '../app/layout/RightPanel';
import useAppSelector from '../app/redux/useAppSelector';
import Button from '../components/Button';
import EmptyState from '../components/EmptyState';
import Menu, { MenuItem, MenuSeparator } from '../components/Menu';
import OpacityMotionTransition from '../components/OpacityMotionTransition';
import PopOverPrimitive from '../components/PopOverPrimitive';
import SVGIcon from '../components/SVGIcon';
import Tooltip from '../components/Tooltip';
import { useMarkdownToHTML } from '../components/useParsedMarkdown';
import CopilotMessageComposer, {
  getMessageTextFromOps,
} from '../editor/copilot/CopilotMessageComposer';
import QuillContext, { useQuill } from '../editor/QuillContext';
import deltaToText from '../editor/utils/deltaToText';
import findClosestScrollingContainer from '../editor/utils/findClosestScrollingContainer';
import arrowDownIcon from '../icons/non-prefilled/16x16/arrowDown.svg';
import clockIcon from '../icons/non-prefilled/16x16/clock.svg';
import commentAddIcon from '../icons/non-prefilled/16x16/commentAdd.svg';
import BetaBadge from '../organization/BetaBadge';
import HoverContentPreview from '../post/HoverContentPreview';
import getPostIdsFromOps from '../utils/getPostIdsFromOps';
import { LinkStateContext } from '../utils/router/Link';

type BaseSystemMessage = {
  id: string;
  type: 'system';
};

export type SystemMessage = BaseSystemMessage &
  (
    | {
        status: 'finished';
        responseId: string;
        text: string;
        sources: Source[];
      }
    | {
        status: 'error';
        error: string;
      }
  );

export type UserMessage = {
  id: string;
  type: 'user';
  content: Op[];
};

export type Message = UserMessage | SystemMessage;

type Conversation = {
  id: string;
  createdAt: string;
  name?: string;
  messages: Message[];
};

type AllConversationsMap = Record<string, Conversation>;

type PendingSystemMessage = BaseSystemMessage & {
  status: 'pending';
};

type StartOrSearchSystemMessage = BaseSystemMessage & {
  status: 'start' | 'search';
  responseId: string;
};

type UpdateSystemMessage = BaseSystemMessage & {
  status: 'update';
  responseId: string;
  text: string;
};

export type GeneratingSystemMessage =
  | PendingSystemMessage
  | StartOrSearchSystemMessage
  | UpdateSystemMessage;

const AI_COPILOT_CONVERSATIONS_KEY = 'ai-copilot-conversations';

const conversationsAtom = atomWithStorage<Record<string, AllConversationsMap>>(
  AI_COPILOT_CONVERSATIONS_KEY,
  {},
);

const useAllConversations = () => {
  const currentUser = useAppSelector((state) => state.session.user);
  const [conversations, setConversations] = useAtom(conversationsAtom);

  const setConversation = useCallback(
    (
      conversationId: string,
      callback: (conversation: Conversation | null) => Conversation | null,
    ) => {
      setConversations((prev) => {
        const conversationMap = prev[currentUser.id] ?? {};
        const conversation = callback(conversationMap[conversationId] ?? null);
        if (!conversation) {
          return {
            ...prev,
            [currentUser.id]: _.omit(conversationMap, conversationId),
          };
        }
        return {
          ...prev,
          [currentUser.id]: {
            ...conversationMap,
            [conversation.id]: conversation,
          },
        };
      });
    },
    [currentUser.id, setConversations],
  );

  const allConversationsMap = useMemo(
    () => conversations[currentUser.id] ?? {},
    [conversations, currentUser.id],
  );

  const clearAllConversations = useCallback(() => {
    setConversations({});
  }, [setConversations]);

  return {
    allConversationsMap,
    setConversation,
    clearAllConversations,
  };
};

interface ScrollToBottomButtonProps {
  isScrolledToBottom: boolean;
  onClick: () => void;
}

const ScrollToBottomButton = ({
  isScrolledToBottom,
  onClick,
}: ScrollToBottomButtonProps) => {
  return (
    <AnimatePresence>
      {!isScrolledToBottom && (
        <OpacityMotionTransition
          className={styles.scrollToBottomButtonContainer}
        >
          <Button
            aria-label="Scroll to bottom"
            onClick={onClick}
            className={styles.scrollToBottomButton}
          >
            <SVGIcon size={16}>{arrowDownIcon}</SVGIcon>
          </Button>
        </OpacityMotionTransition>
      )}
    </AnimatePresence>
  );
};

const useIsScrolledToBottom = (containerRef: React.RefObject<HTMLElement>) => {
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);

  useEffect(() => {
    const messagesContainer = containerRef.current;
    if (!messagesContainer) return undefined;

    const scrollContainer = findClosestScrollingContainer(containerRef.current);
    if (!scrollContainer) return undefined;

    const handleScrollOrResize = () => {
      setIsScrolledToBottom(
        scrollContainer.scrollTop + scrollContainer.clientHeight >=
          scrollContainer.scrollHeight - MESSAGES_LIST_PADDING_BOTTOM,
      );
    };

    const observer = new ResizeObserver(handleScrollOrResize);
    observer.observe(messagesContainer);

    scrollContainer.addEventListener('scroll', handleScrollOrResize);

    return () => {
      observer.disconnect();
      scrollContainer.removeEventListener('scroll', handleScrollOrResize);
    };
  }, [containerRef]);

  return isScrolledToBottom;
};

const scrollToMessage = (messageId: string, messagesContainer: HTMLElement) => {
  const messageElement = getMessageElementById(messageId);
  if (!messageElement) return;

  const scrollContainer = findClosestScrollingContainer(messageElement);
  if (!scrollContainer) return;

  const messageRect = messageElement.getBoundingClientRect();
  const messagesContainerRect = messagesContainer.getBoundingClientRect();
  const scrollTop = messageRect.top - messagesContainerRect.top - 16;

  scrollContainer.scrollTo({
    top: scrollTop,
    behavior: 'smooth',
  });
};

const MAX_CONVERSATIONS_TO_SHOW = 10;
const DEFAULT_CONVERSATION_NAME = 'New chat';

const getConversationName = (conversation: Conversation) => {
  if (conversation.name) {
    return conversation.name;
  }

  const firstMessage = conversation.messages[0];

  return firstMessage?.type === 'user'
    ? deltaToText(firstMessage.content) || DEFAULT_CONVERSATION_NAME
    : DEFAULT_CONVERSATION_NAME;
};

interface HistoricalConversationsMenuProps {
  allConversationsMap: AllConversationsMap;
  onConversationClick: (conversationId: string) => void;
  onClearAllHistoryClick: () => void;
  disabled: boolean;
  children: React.ReactNode;
}

const HistoricalConversationsMenu = ({
  allConversationsMap,
  onConversationClick,
  onClearAllHistoryClick,
  disabled,
  children,
}: HistoricalConversationsMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const sortedConversations = Object.values(allConversationsMap).toSorted(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
  const recentConversations = sortedConversations.slice(
    0,
    MAX_CONVERSATIONS_TO_SHOW,
  );

  const handleConversationClick = useCallback(
    (conversationId: string) => {
      onConversationClick(conversationId);
      setIsOpen(false);
    },
    [onConversationClick],
  );

  if (disabled) {
    return children;
  }

  return (
    <PopOverPrimitive
      trigger="click"
      placement="bottom-end"
      offset={6}
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <PopOverPrimitive.Trigger>{children}</PopOverPrimitive.Trigger>
      <PopOverPrimitive.Floating>
        <PopOverPrimitive.Content>
          <Menu>
            {recentConversations.map((historicalConversation) => {
              return (
                <MenuItem
                  className={styles.menuItem}
                  key={historicalConversation.id}
                  onClick={() =>
                    handleConversationClick(historicalConversation.id)
                  }
                >
                  {getConversationName(historicalConversation)}
                </MenuItem>
              );
            })}
            <MenuSeparator />
            <MenuItem
              className={styles.menuItem}
              key="new"
              onClick={() => handleConversationClick('new')}
            >
              Start new chat
            </MenuItem>
            <MenuItem
              danger
              disabled={sortedConversations.length === 0}
              onClick={() => {
                onClearAllHistoryClick();
                setIsOpen(false);
              }}
            >
              Clear chat history
            </MenuItem>
          </Menu>
        </PopOverPrimitive.Content>
      </PopOverPrimitive.Floating>
    </PopOverPrimitive>
  );
};

type InnerAiCopilotChatPanelProps = {
  conversationId: string;
  messages: (Message | GeneratingSystemMessage)[];
  allConversationsMap: AllConversationsMap;
  sendMessage: (delta: Delta, tools: Set<Tool>) => UserMessage | null;
  startNewConversation: () => void;
  openConversationById: (conversationId: string) => void;
  clearAllHistory: () => void;
  stopGenerating: () => void;
  retryAnswer: (message: UserMessage, tools: Set<Tool>) => void;
  retryAnswerInNewChat: (message: UserMessage, tools: Set<Tool>) => void;
  tools: Set<Tool>;
  setTools: React.Dispatch<React.SetStateAction<Set<Tool>>>;
};

// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const InnerAiCopilotChatPanel = ({
  conversationId,
  messages,
  allConversationsMap,
  sendMessage,
  startNewConversation,
  openConversationById,
  clearAllHistory,
  stopGenerating,
  retryAnswer,
  retryAnswerInNewChat,
  tools,
  setTools,
}: InnerAiCopilotChatPanelProps) => {
  const [messageToEdit, setMessageToEdit] = useState<UserMessage | null>(null);
  const lastMessage = messages.at(-1);
  const isGenerating =
    lastMessage?.type === 'system' &&
    lastMessage.status !== 'finished' &&
    lastMessage.status !== 'error';

  const markdownToHTML = useMarkdownToHTML();
  const { quill } = useQuill();

  const resetMessageToEdit = useCallback(() => {
    setMessageToEdit((current) => {
      if (current && quill) {
        quill.setContents([]);
        quill.focus();
      }
      return null;
    });
  }, [quill]);

  useEffect(() => {
    resetMessageToEdit();
  }, [conversationId, resetMessageToEdit]);

  const footerRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const handleSend = useCallback(() => {
    if (!quill) return;

    const userMessage = sendMessage(quill.getContents(), tools);
    if (userMessage) {
      quill.setContents([]);
      quill.focus();

      setTimeout(() => {
        const messagesContainer = messagesContainerRef.current;
        if (!messagesContainer) return;
        scrollToMessage(userMessage.id, messagesContainer);
      });
    }
  }, [quill, sendMessage, tools]);

  const handleScrollToBottom = useCallback((behavior?: ScrollBehavior) => {
    const scrollContainer = findClosestScrollingContainer(
      messagesContainerRef.current,
    );
    if (!scrollContainer) return;
    scrollContainer.scrollTo({
      top: scrollContainer.scrollHeight,
      behavior,
    });
  }, []);

  useEffect(() => {
    handleScrollToBottom();
  }, [handleScrollToBottom, conversationId]);

  const isScrolledToBottom = useIsScrolledToBottom(messagesContainerRef);

  const isHistoryButtonDisabled =
    messages.length === 0 && Object.keys(allConversationsMap).length === 0;

  return (
    <RightPanel.Inner
      header={
        <RightPanel.NewHeader
          title={
            <>
              AI Copilot
              <span className={styles.badge}>
                <BetaBadge />
              </span>
            </>
          }
          iconButtonsExtra={
            <>
              <Tooltip content="New chat">
                <RightPanel.IconButton
                  icon={commentAddIcon}
                  disabled={messages.length === 0}
                  onClick={() => {
                    startNewConversation();
                    quill?.focus();
                  }}
                  aria-label="New chat"
                />
              </Tooltip>
              <HistoricalConversationsMenu
                allConversationsMap={allConversationsMap}
                onConversationClick={(id) => {
                  if (id === 'new') {
                    startNewConversation();
                  } else {
                    openConversationById(id);
                  }
                  quill?.focus();
                }}
                onClearAllHistoryClick={clearAllHistory}
                disabled={isHistoryButtonDisabled}
              >
                <Tooltip asChild content="Chat history" closeOnMouseDown>
                  <RightPanel.IconButton
                    disabled={isHistoryButtonDisabled}
                    icon={clockIcon}
                    aria-label="Chat history"
                  />
                </Tooltip>
              </HistoricalConversationsMenu>
            </>
          }
        />
      }
      footer={
        <div ref={footerRef} className={styles.footer}>
          <ScrollToBottomButton
            isScrolledToBottom={isScrolledToBottom}
            onClick={() => {
              handleScrollToBottom('smooth');
            }}
          />

          <CopilotMessageComposer
            editControl={
              messageToEdit
                ? {
                    onCancel: () => {
                      if (!quill) return;
                      resetMessageToEdit();
                    },
                    onEdit: () => {
                      if (!quill) return;
                      retryAnswer(
                        {
                          ...messageToEdit,
                          content: quill.getContents().ops,
                        },
                        tools,
                      );
                      if (messagesContainerRef.current) {
                        scrollToMessage(
                          messageToEdit.id,
                          messagesContainerRef.current,
                        );
                      }
                      resetMessageToEdit();
                    },
                  }
                : undefined
            }
            isGenerating={isGenerating}
            onSend={handleSend}
            onStop={stopGenerating}
            tools={tools}
            setTools={setTools}
          />
        </div>
      }
    >
      <HoverContentPreview>
        <Messages.Container
          ref={messagesContainerRef}
          hasMessages={messages.length > 0}
          footerRef={footerRef}
        >
          {markdownToHTML && (
            <Messages.List messages={messages}>
              {(message) => {
                if (message.type === 'system') {
                  return (
                    <Messages.SystemMessage
                      message={message}
                      markdownToHTML={markdownToHTML}
                    />
                  );
                }

                return (
                  <Messages.UserMessage
                    message={message}
                    isEditing={messageToEdit?.id === message.id}
                    overflowMenu={
                      <Messages.UserMessageOverflowMenu
                        onRetry={() => retryAnswer(message, tools)}
                        onRetryInNewChat={() =>
                          retryAnswerInNewChat(message, tools)
                        }
                        onEdit={() => {
                          if (!quill) return;
                          setMessageToEdit(message);
                          quill.setContents(message.content);
                          quill.setSelection(quill.getLength(), 0);
                          quill.focus();
                        }}
                        isGenerating={isGenerating}
                      />
                    }
                  />
                );
              }}
            </Messages.List>
          )}

          {messages.length === 0 && (
            <EmptyState imageName="aiCopilot" noMargin />
          )}
        </Messages.Container>
      </HoverContentPreview>
    </RightPanel.Inner>
  );
};

const findLastSystemMessage = (messages: Message[]) => {
  return messages
    .toReversed()
    .find((m) => m.type === 'system' && m.status !== 'error');
};

type ConversationData = {
  conversationId: string;
  channelId: string;
  generatingSystemMessage: GeneratingSystemMessage | null;
  initialMessage: Delta | null;
};

const newConversationData = (initialMessage?: Delta): ConversationData => ({
  conversationId: crypto.randomUUID(),
  channelId: crypto.randomUUID(),
  generatingSystemMessage: null,
  initialMessage: initialMessage ?? null,
});

const conversationDataAtom = atom<ConversationData>(newConversationData());

export const useConversationData = () => {
  const [conversationData, setCurrentConversationData] =
    useAtom(conversationDataAtom);

  const startNewConversation = useCallback(
    (initialMessage?: Delta) => {
      setCurrentConversationData(newConversationData(initialMessage));
    },
    [setCurrentConversationData],
  );

  return {
    conversationData,
    setCurrentConversationData,
    startNewConversation,
  };
};

const AiCopilotChatPanel = () => {
  const [tools, setTools] = useState<Set<Tool>>(new Set(['slab']));
  const {
    conversationData: {
      conversationId,
      channelId,
      generatingSystemMessage,
      initialMessage,
    },
    setCurrentConversationData,
    startNewConversation,
  } = useConversationData();

  useEffect(() => {
    return () => {
      setCurrentConversationData((current) => ({
        ...current,
        generatingSystemMessage: null,
      }));
    };
  }, [setCurrentConversationData]);

  const { allConversationsMap, setConversation, clearAllConversations } =
    useAllConversations();

  const aiCopilot = useAiCopilot({
    channelId,
    onProgress: (responseId, status) => {
      setCurrentConversationData((current) => {
        if (!current.generatingSystemMessage) {
          return {
            ...current,
            generatingSystemMessage: {
              id: crypto.randomUUID(),
              type: 'system',
              status: 'start',
              responseId,
            },
          };
        }

        if (current.generatingSystemMessage.status === 'pending') {
          return {
            ...current,
            generatingSystemMessage: {
              ...current.generatingSystemMessage,
              responseId,
              status,
            },
          };
        }

        if (
          (current.generatingSystemMessage.status === 'start' ||
            current.generatingSystemMessage.status === 'search') &&
          current.generatingSystemMessage.responseId === responseId
        ) {
          return {
            ...current,
            generatingSystemMessage: {
              ...current.generatingSystemMessage,
              status,
            },
          };
        }

        return current;
      });
    },
    onAnswerText: (responseId, text) => {
      setCurrentConversationData((current) => {
        if (
          current.generatingSystemMessage &&
          current.generatingSystemMessage.status !== 'pending' &&
          current.generatingSystemMessage.responseId === responseId
        ) {
          if (current.generatingSystemMessage.status === 'update') {
            return {
              ...current,
              generatingSystemMessage: {
                ...current.generatingSystemMessage,
                text: current.generatingSystemMessage.text + text,
              },
            };
          }

          return {
            ...current,
            generatingSystemMessage: {
              ...current.generatingSystemMessage,
              status: 'update',
              text,
            },
          };
        }

        return current;
      });
    },
    onError: (error) => {
      setCurrentConversationData((current) => ({
        ...current,
        generatingSystemMessage: null,
      }));

      setConversation(conversationId, (current) => {
        if (!current) return current;
        return {
          ...current,
          messages: [
            ...current.messages,
            {
              id: crypto.randomUUID(),
              status: 'error',
              error,
              type: 'system',
            },
          ],
        };
      });
    },
    onFinish: (responseId, sources, text) => {
      setCurrentConversationData((current) => ({
        ...current,
        generatingSystemMessage: null,
      }));

      setConversation(conversationId, (current) => {
        if (!current) return current;
        return {
          ...current,
          messages: [
            ...current.messages,
            {
              id: responseId,
              status: 'finished',
              responseId,
              text,
              sources,
              type: 'system',
            },
          ],
        };
      });
    },
  });

  const retryAnswerInNewChat = useCallback(
    (message: UserMessage) => {
      const currentConversation = allConversationsMap[conversationId];
      if (!currentConversation) return;

      const newConversationId = crypto.randomUUID();

      const messageIndex = currentConversation.messages.findIndex(
        (m) => m.id === message.id,
      );
      const messagesToCopy = currentConversation.messages.slice(
        0,
        messageIndex + 1,
      );
      const lastSystemMessage = findLastSystemMessage(messagesToCopy);

      const text = getMessageTextFromOps(message.content);
      const postIds = getPostIdsFromOps(message.content);

      if (
        aiCopilot.sendMessage({
          input: text,
          postIds,
          tools,
          retry: true,
          previousId: lastSystemMessage?.id ?? null,
        })
      ) {
        setConversation(newConversationId, () => {
          return {
            id: newConversationId,
            createdAt: new Date().toISOString(),
            name: `(Copy) ${getConversationName(currentConversation)}`,
            messages: messagesToCopy,
          };
        });

        setCurrentConversationData((current) => ({
          // we keep the current channelId so that the channel connection is not lost
          channelId: current.channelId,
          conversationId: newConversationId,
          generatingSystemMessage: {
            id: crypto.randomUUID(),
            type: 'system',
            status: 'pending',
          },
          initialMessage: null,
        }));
      }
    },
    [
      setConversation,
      aiCopilot,
      allConversationsMap,
      conversationId,
      setCurrentConversationData,
      tools,
    ],
  );

  const retryAnswer = useCallback(
    (message: UserMessage) => {
      const currentConversation = allConversationsMap[conversationId];
      if (!currentConversation) return;

      const messageIndex = currentConversation.messages.findIndex(
        (m) => m.id === message.id,
      );
      const newMessages = currentConversation.messages.slice(0, messageIndex);
      const lastSystemMessage = findLastSystemMessage(newMessages);

      const text = getMessageTextFromOps(message.content);
      const postIds = getPostIdsFromOps(message.content);

      if (
        aiCopilot.sendMessage({
          input: text,
          postIds,
          tools,
          retry: true,
          previousId: lastSystemMessage?.id ?? null,
        })
      ) {
        setConversation(conversationId, (current) => {
          if (!current) return current;

          return {
            ...current,
            messages: [...newMessages, message],
          };
        });

        setCurrentConversationData((current) => ({
          ...current,
          generatingSystemMessage: {
            id: crypto.randomUUID(),
            type: 'system',
            status: 'pending',
          },
        }));
      }
    },
    [
      aiCopilot,
      setConversation,
      conversationId,
      allConversationsMap,
      setCurrentConversationData,
      tools,
    ],
  );

  const sendMessage = useCallback(
    (delta: Delta) => {
      const currentConversation = allConversationsMap[conversationId];
      const lastSystemMessage = currentConversation
        ? findLastSystemMessage(currentConversation.messages)
        : null;

      const text = getMessageTextFromOps(delta.ops);
      const postIds = getPostIdsFromOps(delta.ops);

      if (
        aiCopilot.sendMessage({
          input: text,
          postIds,
          tools,
          previousId: lastSystemMessage?.id ?? null,
        })
      ) {
        const userMessage: UserMessage = {
          id: crypto.randomUUID(),
          type: 'user',
          content: delta.ops,
        };

        setConversation(conversationId, (current) => {
          if (!current) {
            return {
              id: conversationId,
              createdAt: new Date().toISOString(),
              messages: [userMessage],
            };
          }

          return {
            ...current,
            messages: [...current.messages, userMessage],
          };
        });

        setCurrentConversationData((current) => ({
          ...current,
          generatingSystemMessage: {
            id: crypto.randomUUID(),
            type: 'system',
            status: 'pending',
          },
        }));

        return userMessage;
      }

      return null;
    },
    [
      aiCopilot,
      conversationId,
      setConversation,
      setCurrentConversationData,
      allConversationsMap,
      tools,
    ],
  );

  const clearAllHistory = useCallback(() => {
    clearAllConversations();
    startNewConversation();
  }, [clearAllConversations, startNewConversation]);

  const stopGenerating = useCallback(() => {
    setCurrentConversationData((current) => ({
      ...current,
      // we regenerate the channelId when stopping, so that we don't
      // have to deal with update messages from the previous prompt
      channelId: crypto.randomUUID(),
      generatingSystemMessage: null,
    }));
  }, [setCurrentConversationData]);

  const messages = useMemo(() => {
    const conversationMessages = allConversationsMap[conversationId]?.messages;
    if (!conversationMessages) return [];

    return generatingSystemMessage
      ? [...conversationMessages, generatingSystemMessage]
      : conversationMessages;
  }, [allConversationsMap, conversationId, generatingSystemMessage]);

  const hasMessages = messages.length > 0;

  useEffect(() => {
    if (initialMessage && !hasMessages) {
      sendMessage(initialMessage);
    }
  }, [initialMessage, sendMessage, hasMessages]);

  const linkState = useMemo(() => ({ keepCopilotOpen: true }), []);

  return (
    <QuillContext>
      <LinkStateContext.Provider value={linkState}>
        <InnerAiCopilotChatPanel
          conversationId={conversationId}
          allConversationsMap={allConversationsMap}
          messages={messages}
          sendMessage={sendMessage}
          startNewConversation={startNewConversation}
          clearAllHistory={clearAllHistory}
          openConversationById={(id) => {
            setCurrentConversationData({
              conversationId: id,
              channelId: crypto.randomUUID(),
              generatingSystemMessage: null,
              initialMessage: null,
            });
          }}
          stopGenerating={stopGenerating}
          retryAnswer={retryAnswer}
          retryAnswerInNewChat={retryAnswerInNewChat}
          tools={tools}
          setTools={setTools}
        />
      </LinkStateContext.Provider>
    </QuillContext>
  );
};

export default AiCopilotChatPanel;
